import { createFileRoute } from "@tanstack/react-router";

import { ServicePageLayout } from "../components/site/ServicePageLayout";
import { ContactForm } from "../components/home/ContactForm";
import { ClientsMarquee } from "../components/shared/ClientsMarquee";
import { Reviews } from "../components/shared/Reviews";
import { Faq } from "../components/shared/Faq";
import { GeoHero } from "../components/geo/GeoHero";
import { GeoMarquee } from "../components/geo/GeoMarquee";
import { GeoWhy } from "../components/geo/GeoWhy";
import { GeoCard } from "../components/geo/GeoCard";
import { GeoFormats } from "../components/geo/GeoFormats";
import { GeoCalc } from "../components/geo/GeoCalc";
import { GeoProcess } from "../components/geo/GeoProcess";
import { GeoAnalytics } from "../components/geo/GeoAnalytics";
import { GeoEvidence, type GeoEvidenceItem } from "../components/geo/GeoEvidence";
import { GeoIndustry } from "../components/geo/GeoIndustry";
import { GeoLinks } from "../components/geo/GeoLinks";
import { buildMeta, breadcrumbJsonLd, serviceJsonLd, SITE_URL } from "../lib/site";
import { crumbTuples } from "../lib/breadcrumbs";
import { routes } from "../lib/nav";

const title = "Продвижение в Яндекс Картах и 2ГИС — стоимость и ведение | HAY.agency";
const description =
  "Оптимизируем карточки компаний, запускаем рекламу в Яндекс Картах и 2ГИС, работаем с отзывами и аналитикой. Продвижение одной точки и сетей филиалов.";

const geoEvidenceItems: GeoEvidenceItem[] = [];

const faqItems = [
  {
    q: "Сколько стоит продвижение в Яндекс Картах и 2ГИС?",
    a: "Стоимость зависит от количества карточек и филиалов, регионов, состояния данных, объёма работ с отзывами, рекламных форматов и отчётности. Сначала проводим короткий аудит, затем фиксируем состав работ и готовим расчёт. Рекламный бюджет указывается отдельно.",
  },
  {
    q: "Чем бесплатная оптимизация отличается от платного продвижения?",
    a: "Бесплатная оптимизация улучшает полноту и качество карточки без рекламного бюджета площадке: данные, категории, услуги, фотографии, публикации и ответы на отзывы. Платное продвижение добавляет рекламные форматы и оплачиваемый охват. Работа агентства по аудиту, настройке и ведению оплачивается в обоих случаях.",
  },
  {
    q: "Когда ждать результат?",
    a: "Срок зависит от исходного состояния карточек, получения доступов, модерации площадок, числа филиалов и накопления статистики. После аудита мы составляем план запуска и отдельно отмечаем, когда данных уже достаточно для первых выводов.",
  },
  {
    q: "Можно ли гарантировать первое место в выдаче на карте?",
    a: "Нет. Позиция в геосервисах зависит от алгоритмов площадки, расстояния до пользователя, полноты данных, рейтинга и отзывов. Мы влияем на управляемые факторы: качество карточки, рекламные размещения и работу с репутацией.",
  },
  {
    q: "Как работает продвижение сети филиалов?",
    a: "Для сети задаём единый стандарт карточек, проверяем дубли и актуальность данных каждой точки, разделяем кампании и сравниваем доступные показатели филиалов. Бюджет и приоритеты распределяем с учётом задач конкретных адресов.",
  },
  {
    q: "Что делать с негативными отзывами?",
    a: "Мы отслеживаем новые отзывы, готовим корректные ответы и фиксируем повторяющиеся причины недовольства, чтобы бизнес мог устранить их на своей стороне. Удаление объективного негатива площадками не предусмотрено.",
  },
  {
    q: "Нужен ли сайт для продвижения в геосервисах?",
    a: "Не обязательно: карточка организации сама по себе принимает звонки, маршруты и обращения. Но сайт или посадочная страница увеличивают количество сценариев обращения и позволяют подробнее рассказать об услугах.",
  },
  {
    q: "Какие доступы нужны и что будет в отчёте?",
    a: "Нужны согласованные права к карточкам и рекламным кабинетам. Если отслеживаем переходы на сайт и качество заявок, дополнительно подключаем Яндекс Метрику, Roistat или CRM клиента. В отчёте показываем только доступные и согласованные показатели, изменения за период и следующий план работ.",
  },
];

export const Route = createFileRoute("/geo")({
  head: () => {
    const base = buildMeta({
      title,
      description,
      path: "/geo",
      image: `${SITE_URL}/og/services/geo.webp`,
      imageWidth: 1200,
      imageHeight: 630,
      imageAlt: "Реклама и продвижение в Яндекс Картах и 2ГИС — HAY.agency",
      locale: "ru_RU",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": `${SITE_URL}/geo#webpage`,
                url: `${SITE_URL}/geo`,
                name: title,
                description,
                inLanguage: "ru-RU",
                isPartOf: { "@id": `${SITE_URL}/#website` },
                breadcrumb: { "@id": `${SITE_URL}/geo#breadcrumbs` },
                mainEntity: { "@id": `${SITE_URL}/geo#service` },
              },
              serviceJsonLd({
                name: "Продвижение в Яндекс Картах и 2ГИС",
                description,
                path: "/geo",
                id: `${SITE_URL}/geo#service`,
                serviceType: "Продвижение в геосервисах",
                providerId: `${SITE_URL}/#organization`,
                inGraph: true,
              }),
              breadcrumbJsonLd(crumbTuples(routes.geo), {
                id: `${SITE_URL}/geo#breadcrumbs`,
                inGraph: true,
              }),
              {
                "@type": "FAQPage",
                "@id": `${SITE_URL}/geo#faq`,
                mainEntity: faqItems.map((item) => ({
                  "@type": "Question",
                  name: item.q,
                  acceptedAnswer: { "@type": "Answer", text: item.a },
                })),
              },
            ],
          }),
        },
      ],
    };
  },
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout breadcrumb="Геоперформанс" jsonLd={false}>
      <GeoHero />
      <GeoMarquee />
      <GeoWhy />
      <ClientsMarquee bare />
      <GeoCard />
      <GeoFormats />
      <GeoProcess />
      <GeoAnalytics />
      <GeoEvidence items={geoEvidenceItems} />
      <GeoIndustry />
      <GeoCalc />
      <Faq
        items={faqItems}
        compact
        title="Частые вопросы о продвижении в картах"
        lead="Отвечаем на частые вопросы о рекламе и продвижении в Яндекс Картах, Навигаторе и 2ГИС."
      />
      <GeoLinks />
      <Reviews />
      <ContactForm />
    </ServicePageLayout>
  );
}
